import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './components/Header23';
import Footer from './components/Footer23';

const Template23PrivacyPolicy = () => {
    const navigate = useNavigate();

    const sections = [
        {
            title: '1. Information We Collect',
            content: `We collect personal information that you voluntarily provide when creating an account, placing an order, or contacting us. This includes your name, email address, phone number, shipping and billing address, payment information, body measurements (for custom orders), and event/wedding dates. We also automatically collect certain technical data such as IP address, browser type, device information, and browsing behavior on our site through cookies and similar technologies.`,
        },
        {
            title: '2. How We Use Your Information',
            content: `Your personal information is used to process and fulfill orders, manage your account, communicate about your purchases and delivery status, send promotional offers and new collection updates (with your consent), improve our products and services, provide custom sizing and bridal consultation services, and comply with legal obligations. We use body measurements and event dates solely for the purpose of creating and delivering your custom garments on time.`,
        },
        {
            title: '3. Information Sharing',
            content: `We do not sell, trade, or rent your personal information to third parties. We may share your data with trusted service providers who assist us in operating our website, processing payments, and delivering orders. These partners are contractually obligated to protect your data. We may disclose information when required by law or to protect the rights, safety, or property of our company, customers, or the public.`,
        },
        {
            title: '4. Payment Security',
            content: `All payment transactions are processed through secure, PCI-DSS compliant payment gateways. We do not store your full credit card number, CVV, or other sensitive payment data on our servers. Payment processing is handled by industry-leading third-party providers including Razorpay and Stripe, which employ advanced encryption technologies to protect your financial information.`,
        },
        {
            title: '5. Cookies & Tracking',
            content: `We use cookies and similar tracking technologies to enhance your browsing experience, remember your preferences, analyze site traffic, and personalize content. Essential cookies are required for the site to function properly (e.g., shopping cart, login sessions). Analytics cookies help us understand how visitors interact with our site. Marketing cookies are used to deliver relevant advertisements. You can manage cookie preferences through your browser settings. Disabling cookies may affect certain site functionalities.`,
        },
        {
            title: '6. Data Retention',
            content: `We retain your personal information for as long as your account is active or as needed to fulfill orders and provide services. Order history and transaction records are kept for a minimum of 5 years for tax and legal compliance. If you request account deletion, we will remove your personal data within 30 days, except where retention is required by law.`,
        },
        {
            title: '7. Your Rights',
            content: `You have the right to access, update, or correct your personal information at any time through your account settings. You may request deletion of your account and associated data. You can opt out of marketing communications at any time by clicking the "unsubscribe" link in our emails or updating your notification preferences. You may request a copy of the personal data we hold about you.`,
        },
        {
            title: '8. Data Security',
            content: `We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes SSL/TLS encryption for all data transmissions, regular security audits, access controls limiting data access to authorized personnel, and secure data storage on encrypted servers.`,
        },
        {
            title: '9. Children\'s Privacy',
            content: `Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that a child under 18 has provided us with personal information, we will take steps to delete such information promptly.`,
        },
        {
            title: '10. International Data Transfers',
            content: `If you are accessing our website from outside India, your information may be transferred to and processed in India. By using our services, you consent to this transfer. We ensure that appropriate safeguards are in place to protect your data in accordance with applicable data protection laws.`,
        },
        {
            title: '11. Third-Party Links',
            content: `Our website may contain links to third-party websites, including social media platforms and payment providers. We are not responsible for the privacy practices of these external sites. We encourage you to read the privacy policies of any third-party sites you visit.`,
        },
        {
            title: '12. Changes to This Policy',
            content: `We may update this Privacy Policy periodically to reflect changes in our practices, technologies, or legal requirements. We will notify you of significant changes by posting a prominent notice on our website or sending an email notification. Your continued use of our services after such changes constitutes acceptance of the updated policy.`,
        },
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Nunito+Sans:wght@300;400;500;600;700&display=swap');
            `}</style>

            <div style={{ minHeight: '100vh', backgroundColor: '#FFFDF8' }}>
                <Header cartCount={0} />

                {/* Hero */}
                <div style={{
                    background: 'linear-gradient(135deg, #1B3A2D 0%, #2D5A45 100%)',
                    textAlign: 'center',
                    padding: '130px 24px 60px',
                }}>
                    <h1 style={{
                        fontFamily: "'Libre Baskerville', Georgia, serif",
                        fontSize: 'clamp(28px, 4vw, 42px)',
                        fontWeight: 700,
                        color: '#FFFFFF',
                        margin: '0 0 12px 0',
                    }}>
                        Privacy Policy
                    </h1>
                    <p style={{
                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                        fontSize: '15px',
                        color: 'rgba(255,255,255,0.7)',
                        margin: 0,
                    }}>
                        Last updated: March 19, 2026
                    </p>
                </div>

                {/* Content */}
                <div style={{
                    maxWidth: '800px',
                    margin: '0 auto',
                    padding: '60px 24px 80px',
                }}>
                    <p style={{
                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                        fontSize: '15px',
                        color: '#5A5A5A',
                        lineHeight: 1.7,
                        margin: '0 0 40px 0',
                    }}>
                        At UTSAV, we are committed to protecting your privacy and ensuring the security of your
                        personal information. This Privacy Policy outlines how we collect, use, store, and protect
                        your data when you use our website and services.
                    </p>

                    {sections.map((section, idx) => (
                        <div key={idx} style={{ marginBottom: '36px' }}>
                            <h2 style={{
                                fontFamily: "'Libre Baskerville', Georgia, serif",
                                fontSize: '20px',
                                fontWeight: 700,
                                color: '#1B3A2D',
                                margin: '0 0 12px 0',
                            }}>
                                {section.title}
                            </h2>
                            <p style={{
                                fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                fontSize: '15px',
                                color: '#5A5A5A',
                                lineHeight: 1.8,
                                margin: 0,
                            }}>
                                {section.content}
                            </p>
                        </div>
                    ))}

                    {/* Contact */}
                    <div style={{
                        backgroundColor: '#F8F3EC',
                        borderRadius: '14px',
                        padding: '28px',
                        marginTop: '48px',
                    }}>
                        <h3 style={{
                            fontFamily: "'Libre Baskerville', Georgia, serif",
                            fontSize: '18px',
                            fontWeight: 700,
                            color: '#1B3A2D',
                            margin: '0 0 12px 0',
                        }}>
                            Privacy Concerns?
                        </h3>
                        <p style={{
                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                            fontSize: '14px',
                            color: '#5A5A5A',
                            lineHeight: 1.6,
                            margin: '0 0 8px 0',
                        }}>
                            If you have any questions about this Privacy Policy or wish to exercise your data rights,
                            please contact our Data Protection Officer at:
                        </p>
                        <p style={{
                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                            fontSize: '14px',
                            color: '#1B3A2D',
                            fontWeight: 600,
                            margin: '0 0 16px 0',
                        }}>
                            privacy@utsav.com
                        </p>
                        <button
                            onClick={() => navigate('/template23/contact')}
                            style={{
                                padding: '10px 24px',
                                backgroundColor: '#1B3A2D',
                                color: '#FFFFFF',
                                border: 'none',
                                borderRadius: '8px',
                                fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                fontSize: '13px',
                                fontWeight: 700,
                                cursor: 'pointer',
                            }}
                        >
                            Contact Us
                        </button>
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default Template23PrivacyPolicy;
